/**
 * Whitelisted command execution on the *user's own machine*, driven by an agent
 * running on the server.
 *
 * Agents live on the server; some resources (an Android emulator, a USB device,
 * a simulator) only exist on the laptop in front of the person. The
 * `taskin_local` MCP family lets an agent run commands there, but only ones a
 * project admin declared and the device itself accepted:
 *
 *  - The project's `localActions` catalog (server side, edited by a holder of
 *    `p.local.manage`) declares what *may* run. Two kinds: an `action` freezes
 *    an `argv[]` and takes typed arguments the agent fills in by name; a
 *    `pattern` allows a token shape (`adb *`) the agent completes with a
 *    command string. Neither is ever handed to a shell.
 *  - Each desktop accepts entries one by one and stores the accepted *snapshot*
 *    locally. The effective whitelist is the intersection of the two, matched
 *    by `computeLocalActionHash`: change the entry server-side and the hash
 *    moves, so the device has to accept it again instead of silently running
 *    something else under a name it once trusted.
 *
 * Everything in this file is pure and dependency-free on purpose: the same hash
 * and the same matcher have to produce the same answer in the server (Node),
 * the desktop main process (Electron) and the web bundle (browser). That is
 * also why the digest below is a hand-rolled SHA-256 rather than `node:crypto`
 * or the async `crypto.subtle` — see `computeLocalActionHash`.
 */
import { Schema } from "effect";

import {
  IsoDateTime,
  NonNegativeInt,
  PositiveInt,
  ProjectId,
  TagId,
  ThreadId,
  TrimmedNonEmptyString,
} from "./baseSchemas";

// ── Limits ───────────────────────────────────────────────────────────

/** Wall-clock budget of one local run. Same default as a background action. */
export const LOCAL_EXEC_DEFAULT_TIMEOUT_MS = 120_000;
export const LOCAL_EXEC_MAX_TIMEOUT_MS = 600_000;

/** Captured output ceiling per run: 512 KiB by default, 1 MiB at most. */
export const LOCAL_EXEC_DEFAULT_MAX_OUTPUT_BYTES = 524_288;
export const LOCAL_EXEC_MAX_OUTPUT_BYTES = 1_048_576;

/** Bounds on the shape of a catalog entry and of a resolved command. */
export const LOCAL_EXEC_MAX_TOKENS = 64;
export const LOCAL_EXEC_MAX_TOKEN_CHARS = 4_000;
export const LOCAL_EXEC_MAX_ARGS = 16;

/**
 * File transfer bounds, deliberately asymmetric.
 *
 * What goes *down* to the device is a build artifact the agent produced in the
 * thread's worktree (an `.apk` on its way to `adb install`): big, opaque, and
 * only ever read by a local binary. What comes *up* becomes an attachment and
 * is handed to a model, so it has to stay small enough to be worth a turn.
 */
export const LOCAL_EXEC_MAX_DOWNLOAD_BYTES = 314_572_800;
export const LOCAL_EXEC_MAX_UPLOAD_BYTES = 26_214_400;

/**
 * Long edge an uploaded image is scaled down to before it leaves the device.
 * A retina screenshot is several times this and costs tokens for detail no
 * model uses.
 */
export const LOCAL_EXEC_UPLOAD_MAX_IMAGE_EDGE = 1_568;

/** Files moving in either direction for a single run. */
export const LOCAL_EXEC_MAX_FILES_PER_REQUEST = 4;

/**
 * A single argv token: a catalog literal, a pattern token, or one resolved
 * argument. NUL and newlines are rejected outright — they are the separators an
 * argv-only pipeline would otherwise carry into a log, a dialog or a file.
 */
// oxlint-disable-next-line no-control-regex -- matching NUL is the point here
const FORBIDDEN_TOKEN_CHARS = /[\u0000\n\r]/;
// oxlint-disable-next-line no-control-regex -- see above
const ALLOWED_TOKEN = /^[^\u0000\n\r]+$/;

const LocalCommandToken = TrimmedNonEmptyString.check(
  Schema.isMaxLength(LOCAL_EXEC_MAX_TOKEN_CHARS),
  Schema.isPattern(ALLOWED_TOKEN),
);

const LocalExecOutputText = Schema.String.check(Schema.isMaxLength(LOCAL_EXEC_MAX_OUTPUT_BYTES));

// ── File markers ─────────────────────────────────────────────────────

/**
 * A file never travels as a path.
 *
 * When an `action` takes a `file` argument, what lands in the resolved argv is
 * an opaque marker — `<<taskin-file:apk>>` — and not a path on anyone's disk.
 * The device downloads the bytes into a temporary directory *it* chose, matches
 * the argv it received against the snapshot its owner accepted (marker
 * included, so the match is over exactly what was shown in the dialog), and
 * only then swaps the marker for its own path. The agent picks *which file of
 * the worktree* travels; where it lands locally is never a value it controls.
 *
 * `<<taskin-outfile>>` is the same trick for the other direction: the path the
 * command is told to write to.
 *
 * Because the substitution is textual, a marker must never reach the device
 * from anywhere but that substitution: catalog literals may not contain one
 * (`validateProjectLocalAction`), agent-supplied values may not contain one
 * (`renderArgValue`, `tokenizeLocalCommand`), and the matcher refuses a marker
 * in any token whose catalog counterpart was not a file hole.
 */
export const LOCAL_EXEC_OUTFILE_ARG_NAME = "outfile";
export const LOCAL_EXEC_OUTFILE_MARKER = "<<taskin-outfile>>";

export const localExecFileMarker = (argName: string): string => `<<taskin-file:${argName}>>`;

/**
 * Deliberately matches the *prefix* rather than a well-formed marker: a value
 * carrying half of one has no legitimate use either, and a prefix test cannot
 * be out-thought by a malformed variant.
 */
const LOCAL_EXEC_MARKER_PREFIX = "<<taskin-";
export const containsLocalExecMarker = (value: string): boolean =>
  value.includes(LOCAL_EXEC_MARKER_PREFIX);

const MARKER_IN_CATALOG_REASON =
  "Un token del catálogo no puede contener marcadores '<<taskin-…>>': los genera el servidor al resolver un argumento de tipo fichero.";

// ── Catalog ──────────────────────────────────────────────────────────

/**
 * Id of a catalog entry. Restricted beyond "non-empty" because it is also a key
 * in the desktop's on-disk acceptance store and travels in push payloads.
 */
export const LocalActionId = TrimmedNonEmptyString.check(
  Schema.isMaxLength(64),
  Schema.isPattern(/^[a-zA-Z0-9][a-zA-Z0-9_-]*$/),
);
export type LocalActionId = typeof LocalActionId.Type;

/** Hex sha-256 of an entry's security fields — see `computeLocalActionHash`. */
export const LocalActionHash = Schema.String.check(Schema.isPattern(/^[0-9a-f]{64}$/));
export type LocalActionHash = typeof LocalActionHash.Type;

/**
 * Whether the device asks its owner before every run.
 *
 * `always_ask` shows a native dialog with the exact argv about to run;
 * `auto` runs it unattended. Part of the hashed security fields: flipping an
 * entry to `auto` server-side must cost a re-acceptance.
 */
export const LocalExecApprovalMode = Schema.Literals(["always_ask", "auto"]);
export type LocalExecApprovalMode = typeof LocalExecApprovalMode.Type;

/**
 * `sync` waits for the process and returns its output. `detached` returns as
 * soon as the process is up and keeps it running (an emulator, a device
 * daemon); it is killed when the thread is archived or `local_exec_stop` names
 * its pid.
 */
export const LocalExecRunMode = Schema.Literals(["sync", "detached"]);
export type LocalExecRunMode = typeof LocalExecRunMode.Type;

/**
 * `file` is the odd one out: its value is not a token but a file of the
 * thread's worktree the agent wants the device to have. It resolves to a
 * marker, never to a path — see the file-markers section above.
 */
export const LocalActionArgType = Schema.Literals(["string", "enum", "number", "boolean", "file"]);
export type LocalActionArgType = typeof LocalActionArgType.Type;

export const LocalActionArgName = TrimmedNonEmptyString.check(
  Schema.isMaxLength(40),
  Schema.isPattern(/^[a-zA-Z][a-zA-Z0-9_]*$/),
);
export type LocalActionArgName = typeof LocalActionArgName.Type;

/** Lowercase, dotted, short: `.apk`, `.png`. Compared case-insensitively. */
const LocalActionFileExtension = TrimmedNonEmptyString.check(
  Schema.isPattern(/^\.[a-z0-9]{1,10}$/),
);

/**
 * One typed argument of an `action` entry. The agent supplies values by name;
 * they are validated here and only then interpolated into the frozen argv.
 *
 * `pattern` (a regex source, anchored when applied) narrows a `string`;
 * `enumValues` is the closed set of a `enum`; `fileExtensions` and `maxBytes`
 * narrow a `file`. All of them are authored by a holder of `p.local.manage`,
 * and all are capped in length so a pathological regex cannot be smuggled in.
 *
 * The two families do not mix: `validateProjectLocalAction` refuses a `file`
 * carrying `pattern`/`enumValues` and any other type carrying
 * `fileExtensions`/`maxBytes`, so a narrowing that would silently do nothing
 * cannot be saved in the first place.
 */
export const LocalActionArgSpec = Schema.Struct({
  name: LocalActionArgName,
  type: LocalActionArgType,
  required: Schema.Boolean,
  enumValues: Schema.optional(
    Schema.Array(TrimmedNonEmptyString.check(Schema.isMaxLength(200))).check(
      Schema.isMaxLength(64),
    ),
  ),
  pattern: Schema.optional(TrimmedNonEmptyString.check(Schema.isMaxLength(200))),
  /** `file` only: what the named file is allowed to be. */
  fileExtensions: Schema.optional(
    Schema.Array(LocalActionFileExtension).check(Schema.isMaxLength(8)),
  ),
  /** `file` only: per-file ceiling, never above the global download cap. */
  maxBytes: Schema.optional(
    PositiveInt.check(Schema.isLessThanOrEqualTo(LOCAL_EXEC_MAX_DOWNLOAD_BYTES)),
  ),
});
export type LocalActionArgSpec = typeof LocalActionArgSpec.Type;

/**
 * Where the bytes a run produces come from.
 *
 * `text` is the ordinary case: stdout/stderr as captured text, nothing
 * transferred. The other two make the device upload a file afterwards:
 *
 *  - `binary-stdout` redirects the raw stdout of the process to a temporary
 *    file without decoding it. It is what makes `adb exec-out screencap -p`
 *    work at all — that command writes a PNG to stdout and cannot be given a
 *    `>` redirection, because nothing here ever goes through a shell.
 *  - `outfile` gives the command a path to write to: the argv carries a single
 *    `{outfile}` hole, which resolves to `<<taskin-outfile>>` and is replaced
 *    by the device with a path inside its own temporary directory.
 *
 * Part of the hashed fields: turning an entry into one that ships a file off
 * the machine is a security change, and costs a re-acceptance.
 */
export const LocalExecOutputMode = Schema.Literals(["text", "binary-stdout", "outfile"]);
export type LocalExecOutputMode = typeof LocalExecOutputMode.Type;

const localActionBaseFields = {
  id: LocalActionId,
  title: TrimmedNonEmptyString.check(Schema.isMaxLength(120)),
  description: Schema.optional(TrimmedNonEmptyString.check(Schema.isMaxLength(500))),
  approval: LocalExecApprovalMode,
  /** Absolute path to run in. `null` means the device's home directory. */
  cwd: Schema.NullOr(TrimmedNonEmptyString.check(Schema.isMaxLength(1_000))),
  timeoutMs: PositiveInt.check(Schema.isLessThanOrEqualTo(LOCAL_EXEC_MAX_TIMEOUT_MS)).pipe(
    Schema.withDecodingDefault(() => LOCAL_EXEC_DEFAULT_TIMEOUT_MS),
  ),
  maxOutputBytes: PositiveInt.check(Schema.isLessThanOrEqualTo(LOCAL_EXEC_MAX_OUTPUT_BYTES)).pipe(
    Schema.withDecodingDefault(() => LOCAL_EXEC_DEFAULT_MAX_OUTPUT_BYTES),
  ),
  mode: LocalExecRunMode,
  output: LocalExecOutputMode.pipe(Schema.withDecodingDefault(() => "text" as LocalExecOutputMode)),
  /**
   * Whether this entry is offered as a starting point when someone declares a
   * command in *another* project.
   *
   * Commands stay per project because `cwd` is a path on one person's machine,
   * so a genuinely shared catalog would be wrong everywhere but where it was
   * written. What is shareable is the shape — the argv, the arguments, the
   * limits — and that is what a template hands over: copied into a new entry
   * with a new id, which the author then adjusts.
   *
   * Out of the hashed fields on purpose, and worth being explicit about: this
   * says nothing about what the command may do on a device, so marking one as a
   * template must not cost every machine a re-acceptance. Also not editable
   * through the catalog: `project.meta.update` carries the stored value forward
   * by id, and only `project.localAction.template.set` — global
   * `p.local.template` — moves it. Otherwise anyone holding `p.local.manage` on
   * one project would be publishing into everyone else's "Añadir comando".
   */
  isTemplate: Schema.Boolean.pipe(Schema.withDecodingDefault(() => false)),
} as const;

/**
 * Frozen argv with typed holes. `argv[0]` is always a literal — the program
 * itself is never chosen by the agent — and every `{name}` placeholder must
 * name a declared argument. Placeholders may sit inside a token
 * (`--serial={serial}`): the token count is fixed by the catalog, so no value
 * can ever introduce another argument.
 */
export const LocalActionCommandEntry = Schema.Struct({
  ...localActionBaseFields,
  kind: Schema.Literal("action"),
  argv: Schema.NonEmptyArray(LocalCommandToken).check(Schema.isMaxLength(LOCAL_EXEC_MAX_TOKENS)),
  args: Schema.Array(LocalActionArgSpec).check(Schema.isMaxLength(LOCAL_EXEC_MAX_ARGS)),
});
export type LocalActionCommandEntry = typeof LocalActionCommandEntry.Type;

/**
 * Token shape the agent completes, e.g. `["adb", "**"]`. `*` stands for exactly
 * one token, `**` for the rest and only as the last token. Position 0 is always
 * a literal, so a pattern authorises one program and nothing else.
 */
export const LocalActionPatternEntry = Schema.Struct({
  ...localActionBaseFields,
  kind: Schema.Literal("pattern"),
  pattern: Schema.NonEmptyArray(LocalCommandToken).check(Schema.isMaxLength(LOCAL_EXEC_MAX_TOKENS)),
});
export type LocalActionPatternEntry = typeof LocalActionPatternEntry.Type;

export const ProjectLocalAction = Schema.Union([LocalActionCommandEntry, LocalActionPatternEntry]);
export type ProjectLocalAction = typeof ProjectLocalAction.Type;

/**
 * One published command, as offered to someone declaring a command elsewhere.
 *
 * Carries the project it came from because that is the only thing that makes
 * the list readable: two projects reasonably both have "Instalar el APK", and
 * the one worth copying is the one from a project like yours. `matchesProject`
 * is the same judgement made by tags — see `matchesTagContext` — resolved
 * server-side so the caller needs no permission on the source project to be
 * shown a sensible order.
 *
 * `entry.cwd` is copied over like everything else, and is the field most likely
 * to be wrong: it is a path on whoever wrote the template's machine. That is
 * why a template prefills a form rather than creating an entry.
 */
export const LocalCommandTemplate = Schema.Struct({
  projectId: ProjectId,
  projectTitle: Schema.String,
  entry: ProjectLocalAction,
  tagIds: Schema.Array(TagId),
  matchesProject: Schema.Boolean,
});
export type LocalCommandTemplate = typeof LocalCommandTemplate.Type;

export const LocalCommandTemplatesResponse = Schema.Struct({
  templates: Schema.Array(LocalCommandTemplate),
});
export type LocalCommandTemplatesResponse = typeof LocalCommandTemplatesResponse.Type;

// ── Wire payloads ────────────────────────────────────────────────────

export const LocalExecRequestId = TrimmedNonEmptyString.check(Schema.isMaxLength(64));
export type LocalExecRequestId = typeof LocalExecRequestId.Type;

export const LocalExecDeviceId = TrimmedNonEmptyString.check(Schema.isMaxLength(64));
export const LocalExecDeviceName = TrimmedNonEmptyString.check(Schema.isMaxLength(120));

const Sha256Hex = Schema.String.check(Schema.isPattern(/^[0-9a-f]{64}$/));

/**
 * Where the device fetches from or uploads to, always as a path *relative to
 * the server it is already talking to*. Absolute URLs are refused on purpose:
 * a payload must not be able to point a desktop at another host, and the
 * desktop already knows the one origin it trusts.
 */
const LocalExecTransferUrl = TrimmedNonEmptyString.check(
  Schema.isMaxLength(2_000),
  Schema.isPattern(/^\/[A-Za-z0-9._~%!$&'()*+,;=:@/?-]*$/),
);

/**
 * A basename, and nothing else: no separator, no `.`/`..`. It is shown in the
 * approval dialog and used to name the device's temporary copy, so it must not
 * be able to express a path.
 */
const LocalExecFileName = TrimmedNonEmptyString.check(
  Schema.isMaxLength(255),
  // oxlint-disable-next-line no-control-regex -- excluding NUL is the point
  Schema.isPattern(/^(?!\.{1,2}$)[^/\\\u0000]+$/),
);

/**
 * One file travelling server → device for this run: the bytes of a worktree
 * file the agent named through a `file` argument.
 *
 * `sha256` and `sizeBytes` are what the *server* measured when it minted the
 * ticket, and the device verifies them before the marker is ever substituted.
 * The pair is also baked into the download token, so a file that changed
 * between mint and fetch is a 409 rather than a silent swap.
 */
export const LocalExecDownloadTicket = Schema.Struct({
  /** The `file` argument this fills in — the marker is `<<taskin-file:…>>`. */
  argName: LocalActionArgName,
  fileName: LocalExecFileName,
  sizeBytes: NonNegativeInt.check(Schema.isLessThanOrEqualTo(LOCAL_EXEC_MAX_DOWNLOAD_BYTES)),
  sha256: Sha256Hex,
  url: LocalExecTransferUrl,
});
export type LocalExecDownloadTicket = typeof LocalExecDownloadTicket.Type;

/**
 * Permission to send *one* file back for this run, valid until `expiresAt`.
 * Present only when the entry's `output` asks for a file; a device that
 * uploads without a ticket has nowhere to upload to.
 */
export const LocalExecUploadTicket = Schema.Struct({
  url: LocalExecTransferUrl,
  maxBytes: PositiveInt.check(Schema.isLessThanOrEqualTo(LOCAL_EXEC_MAX_UPLOAD_BYTES)),
  expiresAt: IsoDateTime,
});
export type LocalExecUploadTicket = typeof LocalExecUploadTicket.Type;

/** One file the device sent back, already persisted as an attachment. */
export const LocalExecUploadedFile = Schema.Struct({
  attachmentId: TrimmedNonEmptyString.check(
    Schema.isMaxLength(128),
    Schema.isPattern(/^[a-z0-9_-]+$/i),
  ),
  mimeType: TrimmedNonEmptyString.check(Schema.isMaxLength(100), Schema.isPattern(/^image\//i)),
  sizeBytes: NonNegativeInt.check(Schema.isLessThanOrEqualTo(LOCAL_EXEC_MAX_UPLOAD_BYTES)),
  name: TrimmedNonEmptyString.check(Schema.isMaxLength(255)),
});
export type LocalExecUploadedFile = typeof LocalExecUploadedFile.Type;

/**
 * Server → desktop, pushed on `localExec.request`.
 *
 * Carries the *resolved* argv (the server builds it from the projected catalog,
 * never from the model's text) plus the id and hash of the entry it came from.
 * The device does not trust any of it as a description of what it may run: it
 * looks the entry up in its own accepted snapshot, compares the hash, and
 * re-matches this argv against that snapshot before spawning anything.
 */
export const LocalExecRequestPayload = Schema.Struct({
  requestId: LocalExecRequestId,
  threadId: ThreadId,
  projectId: ProjectId,
  entryId: LocalActionId,
  entryHash: LocalActionHash,
  argv: Schema.NonEmptyArray(LocalCommandToken).check(Schema.isMaxLength(LOCAL_EXEC_MAX_TOKENS)),
  /**
   * Files the device must fetch before it can substitute the markers in `argv`.
   * Defaults to none when decoding, so a payload minted by an older server
   * still reads as "nothing to transfer" rather than failing.
   */
  downloads: Schema.Array(LocalExecDownloadTicket)
    .check(Schema.isMaxLength(LOCAL_EXEC_MAX_FILES_PER_REQUEST))
    .pipe(Schema.withDecodingDefault(() => [])),
  /** Where the produced file goes, or `null` when the entry produces text. */
  upload: Schema.NullOr(LocalExecUploadTicket).pipe(Schema.withDecodingDefault(() => null)),
  requestedAt: IsoDateTime,
});
export type LocalExecRequestPayload = typeof LocalExecRequestPayload.Type;

/**
 * How a run ended.
 *
 * `rejected` is the owner declining the dialog, `not_accepted` is the device
 * not holding a matching acceptance (unknown entry, or the entry changed
 * server-side), `spawn_failed` is the binary missing. All three are produced by
 * the desktop; the relay produces `error`/`timeout` on its own when no device
 * answers.
 */
export const LocalExecStatus = Schema.Literals([
  "ok",
  "error",
  "timeout",
  "rejected",
  "not_accepted",
  "spawn_failed",
]);
export type LocalExecStatus = typeof LocalExecStatus.Type;

export const LocalExecOutcome = Schema.Struct({
  status: LocalExecStatus,
  exitCode: Schema.NullOr(Schema.Int),
  stdout: LocalExecOutputText,
  stderr: LocalExecOutputText,
  /** True when either stream hit `maxOutputBytes` and the process was killed. */
  truncated: Schema.Boolean,
  /** Set for `detached` runs, so `local_exec_stop` has something to name. */
  pid: Schema.NullOr(PositiveInt),
  /** Which machine answered — the user may have more than one desktop open. */
  deviceName: Schema.NullOr(LocalExecDeviceName),
  /** Failure detail with no process behind it (no device, spawn error, veto). */
  message: Schema.optional(Schema.String.check(Schema.isMaxLength(2_000))),
  durationMs: Schema.optional(NonNegativeInt),
  /**
   * What the device sent back and the server already persisted. Empty for
   * every text run, and empty when decoding an outcome from a desktop that
   * predates file transfer.
   */
  uploads: Schema.Array(LocalExecUploadedFile)
    .check(Schema.isMaxLength(LOCAL_EXEC_MAX_FILES_PER_REQUEST))
    .pipe(Schema.withDecodingDefault(() => [])),
});
export type LocalExecOutcome = typeof LocalExecOutcome.Type;

/** Desktop → server, `localExec.respond`. */
export const LocalExecRespondInput = Schema.Struct({
  requestId: LocalExecRequestId,
  outcome: LocalExecOutcome,
});
export type LocalExecRespondInput = typeof LocalExecRespondInput.Type;

/** One entry this device has accepted, as the relay needs to see it. */
export const LocalExecAcceptedEntry = Schema.Struct({
  entryId: LocalActionId,
  hash: LocalActionHash,
  acceptedAt: IsoDateTime,
});
export type LocalExecAcceptedEntry = typeof LocalExecAcceptedEntry.Type;

/**
 * Desktop → server, `localExec.announce`: "this socket is a desktop belonging
 * to me, on this machine, holding these acceptances". Re-sent on reconnect and
 * whenever the acceptance store changes. The relay keeps it per socket only —
 * it is transport state, not domain state, and it dies with the connection.
 */
export const LocalExecAnnounceInput = Schema.Struct({
  deviceId: LocalExecDeviceId,
  deviceName: LocalExecDeviceName,
  accepted: Schema.Array(LocalExecAcceptedEntry),
});
export type LocalExecAnnounceInput = typeof LocalExecAnnounceInput.Type;

/**
 * Server → desktop, pushed on `localExec.control`: kill something that is still
 * running. Fire-and-forget by design — the thread that started a detached
 * process may be archived, or gone.
 */
export const LocalExecStopThreadControl = Schema.Struct({
  kind: Schema.Literal("stop-thread"),
  threadId: ThreadId,
});
export type LocalExecStopThreadControl = typeof LocalExecStopThreadControl.Type;

export const LocalExecStopProcessControl = Schema.Struct({
  kind: Schema.Literal("stop-process"),
  threadId: ThreadId,
  pid: PositiveInt,
});
export type LocalExecStopProcessControl = typeof LocalExecStopProcessControl.Type;

export const LocalExecControlPayload = Schema.Union([
  LocalExecStopThreadControl,
  LocalExecStopProcessControl,
]);
export type LocalExecControlPayload = typeof LocalExecControlPayload.Type;

export const LocalExecDeviceInfo = Schema.Struct({
  deviceId: LocalExecDeviceId,
  deviceName: LocalExecDeviceName,
});
export type LocalExecDeviceInfo = typeof LocalExecDeviceInfo.Type;

/** Result of the device-side accept flow: the owner may decline the dialog. */
export interface LocalExecAcceptResult {
  readonly accepted: boolean;
  readonly entries: ReadonlyArray<LocalExecAcceptedEntry>;
}

// ── Hashing ──────────────────────────────────────────────────────────

const SHA256_K = [
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
] as const;

/**
 * UTF-8 encode without `TextEncoder`, so this file needs no DOM or Node lib in
 * its typings. Unpaired surrogates become U+FFFD, matching what `TextEncoder`
 * would produce, so a value that ever round-trips through one still hashes the
 * same.
 */
const utf8Bytes = (value: string): Uint8Array => {
  const out: Array<number> = [];
  for (let index = 0; index < value.length; index += 1) {
    let code = value.charCodeAt(index);
    if (code >= 0xd800 && code <= 0xdbff && index + 1 < value.length) {
      const low = value.charCodeAt(index + 1);
      if (low >= 0xdc00 && low <= 0xdfff) {
        code = 0x10000 + ((code - 0xd800) << 10) + (low - 0xdc00);
        index += 1;
      }
    }
    if (code >= 0xd800 && code <= 0xdfff) code = 0xfffd;
    if (code < 0x80) {
      out.push(code);
    } else if (code < 0x800) {
      out.push(0xc0 | (code >> 6), 0x80 | (code & 0x3f));
    } else if (code < 0x10000) {
      out.push(0xe0 | (code >> 12), 0x80 | ((code >> 6) & 0x3f), 0x80 | (code & 0x3f));
    } else {
      out.push(
        0xf0 | (code >> 18),
        0x80 | ((code >> 12) & 0x3f),
        0x80 | ((code >> 6) & 0x3f),
        0x80 | (code & 0x3f),
      );
    }
  }
  return Uint8Array.from(out);
};

const rotr = (value: number, bits: number): number => (value >>> bits) | (value << (32 - bits));

/**
 * SHA-256 over a string, hex encoded.
 *
 * Hand-rolled on purpose. The three runtimes that must agree on this digest are
 * Node, Electron's main process and the browser bundle: `node:crypto` does not
 * exist in the last one, and `crypto.subtle` is asynchronous *and* absent from
 * insecure browsing contexts, which would push an `await` into the relay's
 * device filter and the desktop's accept check for no benefit. The input is a
 * few hundred bytes of canonical JSON, so the cost is irrelevant.
 */
const sha256Hex = (input: string): string => {
  const bytes = utf8Bytes(input);
  const bitLength = bytes.length * 8;
  const paddedLength = Math.ceil((bytes.length + 9) / 64) * 64;
  const buffer = new Uint8Array(paddedLength);
  buffer.set(bytes);
  buffer[bytes.length] = 0x80;
  const view = new DataView(buffer.buffer);
  view.setUint32(paddedLength - 8, Math.floor(bitLength / 0x100000000), false);
  view.setUint32(paddedLength - 4, bitLength >>> 0, false);

  const h = new Uint32Array([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
  ]);
  const w = new Uint32Array(64);

  for (let offset = 0; offset < paddedLength; offset += 64) {
    for (let i = 0; i < 16; i += 1) w[i] = view.getUint32(offset + i * 4, false);
    for (let i = 16; i < 64; i += 1) {
      const w15 = w[i - 15] as number;
      const w2 = w[i - 2] as number;
      const s0 = rotr(w15, 7) ^ rotr(w15, 18) ^ (w15 >>> 3);
      const s1 = rotr(w2, 17) ^ rotr(w2, 19) ^ (w2 >>> 10);
      w[i] = ((w[i - 16] as number) + s0 + (w[i - 7] as number) + s1) >>> 0;
    }

    let [a, b, c, d, e, f, g, hh] = [
      h[0] as number,
      h[1] as number,
      h[2] as number,
      h[3] as number,
      h[4] as number,
      h[5] as number,
      h[6] as number,
      h[7] as number,
    ];

    for (let i = 0; i < 64; i += 1) {
      const s1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
      const ch = (e & f) ^ (~e & g);
      const temp1 = (hh + s1 + ch + (SHA256_K[i] as number) + (w[i] as number)) >>> 0;
      const s0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const temp2 = (s0 + maj) >>> 0;
      hh = g;
      g = f;
      f = e;
      e = (d + temp1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (temp1 + temp2) >>> 0;
    }

    h[0] = ((h[0] as number) + a) >>> 0;
    h[1] = ((h[1] as number) + b) >>> 0;
    h[2] = ((h[2] as number) + c) >>> 0;
    h[3] = ((h[3] as number) + d) >>> 0;
    h[4] = ((h[4] as number) + e) >>> 0;
    h[5] = ((h[5] as number) + f) >>> 0;
    h[6] = ((h[6] as number) + g) >>> 0;
    h[7] = ((h[7] as number) + hh) >>> 0;
  }

  let hex = "";
  for (let i = 0; i < 8; i += 1) hex += (h[i] as number).toString(16).padStart(8, "0");
  return hex;
};

/**
 * Bumped when the set of hashed fields changes, so every device is forced to
 * re-accept rather than trusting an old digest computed over fewer fields.
 *
 * `v2` added file transfer: `output` and the two `file` narrowings. The bump
 * invalidates every acceptance already stored on every desktop, and that is
 * the point — the entries a device accepted under `v1` were accepted by
 * someone who was shown a command that could not move a single byte off or
 * onto their machine. Those runs now can, so the answer has to be asked again.
 */
const LOCAL_ACTION_HASH_VERSION = "taskin.localAction.v2";

/**
 * Canonical JSON of the fields that decide *what may run and under what
 * limits*. Written as a positional array with an explicit field order, so it
 * cannot drift with object key ordering.
 *
 * `title`, `description` and `isTemplate` are deliberately out: the first two
 * are labels, and fixing a typo in one should not invalidate every device's
 * acceptance; the third says where the entry is *offered*, never what it may
 * do. Everything else is in, including `approval` (flipping `always_ask` to `auto` is a security
 * change), the two limits (they bound what a run can cost the machine),
 * `output` (whether a file leaves the machine at all) and each argument's
 * `fileExtensions`/`maxBytes` (which files may be pulled onto it).
 *
 * Exported so the digest can be pinned against a real SHA-256 implementation in
 * tests, and so a device can show *what* it is accepting rather than a hash.
 */
export const localActionCanonicalPayload = (entry: ProjectLocalAction): string => {
  const shape =
    entry.kind === "action"
      ? {
          kind: "action",
          argv: [...entry.argv],
          args: entry.args.map((spec) => ({
            name: spec.name,
            type: spec.type,
            required: spec.required,
            enumValues: spec.enumValues ? [...spec.enumValues] : null,
            pattern: spec.pattern ?? null,
            fileExtensions: spec.fileExtensions ? [...spec.fileExtensions] : null,
            maxBytes: spec.maxBytes ?? null,
          })),
        }
      : { kind: "pattern", pattern: [...entry.pattern] };

  return JSON.stringify([
    LOCAL_ACTION_HASH_VERSION,
    entry.id,
    entry.approval,
    entry.mode,
    entry.cwd,
    entry.timeoutMs,
    entry.maxOutputBytes,
    entry.output,
    shape,
  ]);
};

/**
 * The identity a device's acceptance is pinned to. Same entry, same hash, in
 * any of the three runtimes; any security-relevant edit server-side moves it
 * and the device falls back to `not_accepted` until its owner accepts again.
 */
export const computeLocalActionHash = (entry: ProjectLocalAction): string =>
  sha256Hex(localActionCanonicalPayload(entry));

// ── Tokenising, resolving and matching ───────────────────────────────

export type LocalActionArgValue = string | number | boolean;

/** What the agent asked for: named args for an `action`, text for a `pattern`. */
export interface LocalActionInvocation {
  readonly args?: Readonly<Record<string, LocalActionArgValue>>;
  readonly command?: string;
}

/**
 * One `file` argument the caller has to turn into a download ticket: the argv
 * only carries the marker, so this is where the path it stands for is named.
 */
export interface LocalExecResolvedFile {
  readonly argName: string;
  /** Worktree-relative and normalised: no `..`, no leading `/`, no `./`. */
  readonly path: string;
}

export type LocalExecResolution =
  | {
      readonly ok: true;
      readonly argv: ReadonlyArray<string>;
      readonly files: ReadonlyArray<LocalExecResolvedFile>;
    }
  | { readonly ok: false; readonly reason: string };

export type LocalExecTokenization =
  | { readonly ok: true; readonly tokens: ReadonlyArray<string> }
  | { readonly ok: false; readonly reason: string };

const PLACEHOLDER = /\{([a-zA-Z][a-zA-Z0-9_]*)\}/g;

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Split a command string into argv tokens the way a person means it, and
 * nothing like the way a shell does: single and double quotes group, runs of
 * whitespace separate, and that is the whole grammar. No escapes, no variable
 * expansion, no globbing, no operators — `;`, `|`, `&&` and `$(...)` are just
 * characters here, and since the result is spawned without a shell they stay
 * characters. Control characters are rejected outright.
 */
export const tokenizeLocalCommand = (command: string): LocalExecTokenization => {
  if (FORBIDDEN_TOKEN_CHARS.test(command)) {
    return {
      ok: false,
      reason: "El comando no puede contener saltos de línea ni caracteres nulos.",
    };
  }

  const tokens: Array<string> = [];
  let current = "";
  let quoted = false;
  let quote: string | null = null;

  for (const char of command) {
    if (quote !== null) {
      if (char === quote) quote = null;
      else current += char;
      continue;
    }
    if (char === '"' || char === "'") {
      quote = char;
      quoted = true;
      continue;
    }
    if (/\s/.test(char)) {
      if (current.length > 0 || quoted) {
        tokens.push(current);
        current = "";
        quoted = false;
      }
      continue;
    }
    current += char;
  }

  if (quote !== null) return { ok: false, reason: "El comando tiene una comilla sin cerrar." };
  if (current.length > 0 || quoted) tokens.push(current);

  if (tokens.length === 0) return { ok: false, reason: "El comando está vacío." };
  if (tokens.length > LOCAL_EXEC_MAX_TOKENS) {
    return { ok: false, reason: `El comando supera los ${LOCAL_EXEC_MAX_TOKENS} argumentos.` };
  }
  const oversized = tokens.find((token) => token.length > LOCAL_EXEC_MAX_TOKEN_CHARS);
  if (oversized !== undefined) {
    return {
      ok: false,
      reason: `Un argumento supera los ${LOCAL_EXEC_MAX_TOKEN_CHARS} caracteres.`,
    };
  }
  if (tokens.some((token) => token.length === 0)) {
    return { ok: false, reason: "El comando contiene un argumento vacío." };
  }
  if (tokens.some(containsLocalExecMarker)) {
    return {
      ok: false,
      reason: "El comando no puede contener marcadores '<<taskin-…>>'.",
    };
  }

  return { ok: true, tokens };
};

const compileArgPattern = (spec: LocalActionArgSpec): RegExp | null => {
  if (spec.pattern === undefined) return null;
  try {
    return new RegExp(`^(?:${spec.pattern})$`);
  } catch {
    return null;
  }
};

/** A path is not a token, so it gets its own ceiling. */
const LOCAL_EXEC_MAX_FILE_PATH_CHARS = 512;

/**
 * A `file` value names a file *inside the thread's worktree*, and nothing else.
 *
 * Containment is ultimately decided by the server, which resolves the path and
 * follows symlinks before it reads a byte. What is done here is the cheap,
 * portable half: refuse anything that is not plainly relative, drop the noise
 * (`./`, doubled slashes) so the same file always produces the same string, and
 * hold the value to the extensions the entry's author allowed.
 */
const normalizeWorktreeFilePath = (
  spec: LocalActionArgSpec,
  raw: string,
):
  | { readonly ok: true; readonly path: string }
  | { readonly ok: false; readonly reason: string } => {
  const value = raw.trim();
  if (value.length === 0) return { ok: false, reason: `'${spec.name}' no puede estar vacío.` };
  if (value.length > LOCAL_EXEC_MAX_FILE_PATH_CHARS) {
    return {
      ok: false,
      reason: `La ruta de '${spec.name}' supera los ${LOCAL_EXEC_MAX_FILE_PATH_CHARS} caracteres.`,
    };
  }
  if (FORBIDDEN_TOKEN_CHARS.test(value)) {
    return { ok: false, reason: `'${spec.name}' no puede contener saltos de línea ni nulos.` };
  }
  if (containsLocalExecMarker(value)) {
    return { ok: false, reason: `'${spec.name}' no puede contener marcadores '<<taskin-…>>'.` };
  }
  if (
    value.startsWith("/") ||
    value.startsWith("~") ||
    value.includes("\\") ||
    /^[a-zA-Z]:/.test(value)
  ) {
    return {
      ok: false,
      reason: `'${spec.name}' tiene que ser una ruta relativa dentro del worktree del hilo.`,
    };
  }

  const segments: Array<string> = [];
  for (const segment of value.split("/")) {
    if (segment.length === 0 || segment === ".") continue;
    if (segment === "..") {
      return { ok: false, reason: `'${spec.name}' no puede salir del worktree con '..'.` };
    }
    segments.push(segment);
  }
  if (segments.length === 0) {
    return { ok: false, reason: `'${spec.name}' no nombra ningún fichero.` };
  }

  const allowed = spec.fileExtensions ?? [];
  if (allowed.length > 0) {
    const fileName = (segments[segments.length - 1] as string).toLowerCase();
    if (!allowed.some((extension) => fileName.endsWith(extension.toLowerCase()))) {
      return {
        ok: false,
        reason: `'${spec.name}' tiene que ser un fichero con extensión ${allowed.join(", ")}.`,
      };
    }
  }

  return { ok: true, path: segments.join("/") };
};

type RenderedArgValue =
  | { readonly ok: true; readonly value: string; readonly filePath?: string }
  | { readonly ok: false; readonly reason: string };

/** Validate one supplied value against its spec and render it as a token. */
const renderArgValue = (spec: LocalActionArgSpec, raw: LocalActionArgValue): RenderedArgValue => {
  let value: string;

  switch (spec.type) {
    case "file": {
      if (typeof raw !== "string") {
        return {
          ok: false,
          reason: `'${spec.name}' tiene que ser la ruta de un fichero del worktree del hilo.`,
        };
      }
      const normalized = normalizeWorktreeFilePath(spec, raw);
      if (!normalized.ok) return normalized;
      // The token is the marker, never the path: the device decides where the
      // bytes land, and the argv it matches is the one its owner accepted.
      return { ok: true, value: localExecFileMarker(spec.name), filePath: normalized.path };
    }
    case "boolean": {
      if (typeof raw !== "boolean") {
        return { ok: false, reason: `'${spec.name}' tiene que ser true o false.` };
      }
      value = raw ? "true" : "false";
      break;
    }
    case "number": {
      if (typeof raw !== "number" || !Number.isFinite(raw)) {
        return { ok: false, reason: `'${spec.name}' tiene que ser un número.` };
      }
      value = String(raw);
      break;
    }
    case "enum": {
      if (typeof raw !== "string") {
        return { ok: false, reason: `'${spec.name}' tiene que ser uno de los valores permitidos.` };
      }
      const allowed = spec.enumValues ?? [];
      if (!allowed.includes(raw)) {
        return {
          ok: false,
          reason: `'${spec.name}' sólo admite: ${allowed.join(", ") || "(sin valores configurados)"}.`,
        };
      }
      value = raw;
      break;
    }
    case "string": {
      if (typeof raw !== "string") {
        return { ok: false, reason: `'${spec.name}' tiene que ser texto.` };
      }
      const compiled = compileArgPattern(spec);
      if (spec.pattern !== undefined && compiled === null) {
        return { ok: false, reason: `El patrón configurado para '${spec.name}' no es válido.` };
      }
      if (compiled !== null && !compiled.test(raw)) {
        return { ok: false, reason: `'${spec.name}' no cumple el formato exigido.` };
      }
      value = raw;
      break;
    }
  }

  if (value.length === 0) return { ok: false, reason: `'${spec.name}' no puede estar vacío.` };
  if (FORBIDDEN_TOKEN_CHARS.test(value)) {
    return { ok: false, reason: `'${spec.name}' no puede contener saltos de línea ni nulos.` };
  }
  // A value that carries a marker would be substituted by the device as if the
  // catalog had put it there; only `file` arguments may produce one.
  if (containsLocalExecMarker(value)) {
    return { ok: false, reason: `'${spec.name}' no puede contener marcadores '<<taskin-…>>'.` };
  }
  if (value.length > LOCAL_EXEC_MAX_TOKEN_CHARS) {
    return {
      ok: false,
      reason: `'${spec.name}' supera los ${LOCAL_EXEC_MAX_TOKEN_CHARS} caracteres.`,
    };
  }
  return { ok: true, value };
};

/**
 * The regex a resolved token must satisfy for a given argument type, ready to
 * be embedded inside a larger token pattern.
 *
 * The author's own `pattern` is stripped of its outer anchors before being
 * embedded: it is written to describe a whole value, and a `^` in the middle of
 * `--serial=(?:^…$)` would never match. `renderArgValue` anchors it explicitly
 * for the same reason, so both paths agree on "the whole value or nothing".
 */
const argValuePattern = (spec: LocalActionArgSpec): string => {
  switch (spec.type) {
    // A resolved `file` token is the marker and only the marker: the path the
    // device substitutes later is not part of what anyone matched or accepted.
    case "file":
      return `(?:${escapeRegExp(localExecFileMarker(spec.name))})`;
    case "boolean":
      return "(?:true|false)";
    case "number":
      return "-?(?:0|[1-9][0-9]*)(?:\\.[0-9]+)?(?:[eE][+-]?[0-9]+)?";
    case "enum":
      return `(?:${(spec.enumValues ?? []).map(escapeRegExp).join("|") || "(?!)"})`;
    case "string":
      return spec.pattern !== undefined && compileArgPattern(spec) !== null
        ? `(?:${stripOuterAnchors(spec.pattern)})`
        : "[^\\u0000\\n\\r]+";
  }
};

/** Drop a leading `^` / trailing unescaped `$` so a pattern can be embedded. */
const stripOuterAnchors = (pattern: string): string => {
  const withoutStart = pattern.startsWith("^") ? pattern.slice(1) : pattern;
  return /(?:^|[^\\])(?:\\\\)*\$$/.test(withoutStart) ? withoutStart.slice(0, -1) : withoutStart;
};

const placeholderNames = (token: string): ReadonlyArray<string> => {
  const names: Array<string> = [];
  for (const match of token.matchAll(PLACEHOLDER)) names.push(match[1] as string);
  return names;
};

/**
 * Structural invariants a catalog entry must satisfy before it is stored or
 * used. Kept here rather than in the decider alone so the desktop and the web
 * editor can refuse the same shapes the server refuses.
 */
export const validateProjectLocalAction = (
  entry: ProjectLocalAction,
): { readonly ok: true } | { readonly ok: false; readonly reason: string } => {
  if (entry.kind === "pattern") {
    if (entry.pattern.some(containsLocalExecMarker)) {
      return { ok: false, reason: MARKER_IN_CATALOG_REASON };
    }
    // A pattern has no typed arguments and no holes, so it has no way to name a
    // file in either direction.
    if (entry.output !== "text") {
      return {
        ok: false,
        reason: "Una entrada de tipo patrón sólo puede devolver texto, no un fichero.",
      };
    }
    const [head, ...rest] = entry.pattern;
    if (head === "*" || head === "**") {
      return { ok: false, reason: "El primer token del patrón tiene que ser el programa literal." };
    }
    const restIndex = rest.findIndex((token) => token === "**");
    if (restIndex !== -1 && restIndex !== rest.length - 1) {
      return { ok: false, reason: "'**' sólo puede ir como último token del patrón." };
    }
    return { ok: true };
  }

  if (entry.argv.some(containsLocalExecMarker)) {
    return { ok: false, reason: MARKER_IN_CATALOG_REASON };
  }

  const [program, ...rest] = entry.argv;
  if (placeholderNames(program).length > 0) {
    return { ok: false, reason: "El primer elemento de argv tiene que ser el programa literal." };
  }
  const declared = new Set(entry.args.map((spec) => spec.name));
  if (declared.size !== entry.args.length) {
    return { ok: false, reason: "Hay argumentos repetidos en la definición." };
  }
  if (declared.has(LOCAL_EXEC_OUTFILE_ARG_NAME)) {
    return {
      ok: false,
      reason: `'${LOCAL_EXEC_OUTFILE_ARG_NAME}' es un nombre reservado: nombra el fichero de salida, no un argumento.`,
    };
  }
  for (const token of rest) {
    for (const name of placeholderNames(token)) {
      if (name === LOCAL_EXEC_OUTFILE_ARG_NAME) continue;
      if (!declared.has(name)) {
        return { ok: false, reason: `El argv usa '{${name}}', que no está declarado en args.` };
      }
    }
  }
  const used = new Set(rest.flatMap((token) => placeholderNames(token)));
  const unused = entry.args.find((spec) => !used.has(spec.name));
  if (unused !== undefined) {
    return {
      ok: false,
      reason: `El argumento '${unused.name}' no se usa en ningún token de argv.`,
    };
  }

  // `outfile` and the output mode describe the same thing from two sides; an
  // entry where they disagree would either write nowhere or upload nothing.
  const outfileHoles = rest.reduce(
    (count, token) =>
      count + placeholderNames(token).filter((name) => name === LOCAL_EXEC_OUTFILE_ARG_NAME).length,
    0,
  );
  if (entry.output === "outfile" && outfileHoles !== 1) {
    return {
      ok: false,
      reason:
        "Con salida en fichero, el argv tiene que usar '{outfile}' exactamente una vez: es donde el dispositivo pone la ruta del temporal.",
    };
  }
  if (entry.output !== "outfile" && outfileHoles > 0) {
    return {
      ok: false,
      reason: "'{outfile}' sólo puede usarse cuando la salida de la acción es un fichero.",
    };
  }
  // Nothing collects the file of a process nobody is waiting for.
  if (entry.output !== "text" && entry.mode !== "sync") {
    return {
      ok: false,
      reason: "Una acción que devuelve un fichero tiene que ejecutarse en modo síncrono.",
    };
  }

  let fileArgs = 0;
  for (const spec of entry.args) {
    if (spec.type === "file") {
      fileArgs += 1;
      if (spec.enumValues !== undefined || spec.pattern !== undefined) {
        return {
          ok: false,
          reason: `El argumento '${spec.name}' es de tipo fichero: no admite patrón ni valores enumerados.`,
        };
      }
      if (spec.maxBytes !== undefined && spec.maxBytes > LOCAL_EXEC_MAX_DOWNLOAD_BYTES) {
        return {
          ok: false,
          reason: `El tamaño máximo de '${spec.name}' supera el límite de ${LOCAL_EXEC_MAX_DOWNLOAD_BYTES} bytes.`,
        };
      }
      continue;
    }
    if (spec.fileExtensions !== undefined || spec.maxBytes !== undefined) {
      return {
        ok: false,
        reason: `El argumento '${spec.name}' no es de tipo fichero: no admite extensiones ni tamaño máximo.`,
      };
    }
    if (spec.type === "enum" && (spec.enumValues ?? []).length === 0) {
      return { ok: false, reason: `El argumento '${spec.name}' es enum pero no tiene valores.` };
    }
    if (spec.pattern !== undefined && compileArgPattern(spec) === null) {
      return {
        ok: false,
        reason: `El patrón de '${spec.name}' no es una expresión regular válida.`,
      };
    }
  }
  if (fileArgs > LOCAL_EXEC_MAX_FILES_PER_REQUEST) {
    return {
      ok: false,
      reason: `Una acción no puede llevar más de ${LOCAL_EXEC_MAX_FILES_PER_REQUEST} argumentos de tipo fichero.`,
    };
  }

  return { ok: true };
};

/**
 * Build the argv the device will be asked to run, from what the agent supplied.
 *
 * For an `action` the frozen tokens are kept as they are and only the
 * placeholders inside them are substituted with validated values, so the number
 * of arguments is decided by the catalog and never by the model. A token that
 * is *exactly* one placeholder of a non-required argument disappears when that
 * argument is absent, which is what makes optional flags expressible.
 *
 * For a `pattern` the agent's text is tokenised and then matched; nothing is
 * built from it beyond the tokens themselves.
 */
export const resolveLocalActionArgv = (
  entry: ProjectLocalAction,
  invocation: LocalActionInvocation,
): LocalExecResolution => {
  const structure = validateProjectLocalAction(entry);
  if (!structure.ok) return structure;

  if (entry.kind === "pattern") {
    const command = invocation.command ?? "";
    if (command.trim().length === 0) {
      return { ok: false, reason: "Falta 'command': el comando completo que quieres ejecutar." };
    }
    const tokenized = tokenizeLocalCommand(command);
    if (!tokenized.ok) return tokenized;
    const matched = matchLocalActionCommand(entry, tokenized.tokens);
    if (!matched.ok) return matched;
    return { ok: true, argv: tokenized.tokens, files: [] };
  }

  const supplied = invocation.args ?? {};
  const unknown = Object.keys(supplied).find(
    (name) => !entry.args.some((spec) => spec.name === name),
  );
  if (unknown !== undefined) {
    return { ok: false, reason: `El argumento '${unknown}' no existe en esta acción.` };
  }

  const rendered = new Map<string, string>();
  const files: Array<LocalExecResolvedFile> = [];
  for (const spec of entry.args) {
    const raw = supplied[spec.name];
    if (raw === undefined || raw === null) {
      if (spec.required) return { ok: false, reason: `Falta el argumento '${spec.name}'.` };
      continue;
    }
    const value = renderArgValue(spec, raw);
    if (!value.ok) return value;
    rendered.set(spec.name, value.value);
    if (value.filePath !== undefined) files.push({ argName: spec.name, path: value.filePath });
  }
  // The one name the agent cannot supply and the catalog cannot declare: the
  // device fills it in with a path of its own once the argv has been matched.
  if (entry.output === "outfile") {
    rendered.set(LOCAL_EXEC_OUTFILE_ARG_NAME, LOCAL_EXEC_OUTFILE_MARKER);
  }

  const argv: Array<string> = [];
  for (const token of entry.argv) {
    const names = placeholderNames(token);
    if (names.length === 0) {
      argv.push(token);
      continue;
    }
    const missing = names.find((name) => !rendered.has(name));
    if (missing !== undefined) {
      // A bare `{opt}` token drops out entirely; anything else cannot be built.
      if (names.length === 1 && token === `{${missing}}`) continue;
      return {
        ok: false,
        reason: `Falta el argumento '${missing}', que forma parte del token '${token}'.`,
      };
    }
    argv.push(token.replace(PLACEHOLDER, (_match, name: string) => rendered.get(name) as string));
  }

  if (argv.length === 0) return { ok: false, reason: "El comando resuelto quedó vacío." };

  const matched = matchLocalActionCommand(entry, argv);
  if (!matched.ok) return matched;
  return { ok: true, argv, files };
};

interface TokenMatcher {
  readonly droppable: boolean;
  readonly rest: boolean;
  readonly test: (token: string) => boolean;
}

const patternMatchers = (entry: LocalActionPatternEntry): ReadonlyArray<TokenMatcher> =>
  entry.pattern.map((token) => {
    if (token === "**") return { droppable: false, rest: true, test: () => true };
    if (token === "*") {
      return { droppable: false, rest: false, test: (candidate: string) => candidate.length > 0 };
    }
    return { droppable: false, rest: false, test: (candidate: string) => candidate === token };
  });

type MatcherBuild =
  | { readonly ok: true; readonly matchers: ReadonlyArray<TokenMatcher> }
  | { readonly ok: false; readonly reason: string };

const actionMatchers = (entry: LocalActionCommandEntry): MatcherBuild => {
  const specs = new Map(entry.args.map((spec) => [spec.name, spec] as const));
  const matchers: Array<TokenMatcher> = [];

  for (const token of entry.argv) {
    const names = placeholderNames(token);
    if (names.length === 0) {
      matchers.push({
        droppable: false,
        rest: false,
        test: (candidate: string) => candidate === token,
      });
      continue;
    }

    let source = "";
    let lastIndex = 0;
    // Whether this token is *supposed* to carry a marker. Every other token is
    // guarded against one below, so a path can never be substituted anywhere
    // the catalog did not put a file hole.
    let carriesMarker = false;
    for (const match of token.matchAll(PLACEHOLDER)) {
      const name = match[1] as string;
      const index = match.index ?? 0;
      source += escapeRegExp(token.slice(lastIndex, index));
      if (name === LOCAL_EXEC_OUTFILE_ARG_NAME) {
        source += escapeRegExp(LOCAL_EXEC_OUTFILE_MARKER);
        carriesMarker = true;
      } else {
        const spec = specs.get(name);
        if (spec === undefined) {
          return { ok: false, reason: `El argv usa '{${name}}', que no está declarado.` };
        }
        source += argValuePattern(spec);
        if (spec.type === "file") carriesMarker = true;
      }
      lastIndex = index + match[0].length;
    }
    source += escapeRegExp(token.slice(lastIndex));

    let regex: RegExp;
    try {
      regex = new RegExp(`^${source}$`);
    } catch {
      return { ok: false, reason: `El token '${token}' produce una expresión regular inválida.` };
    }

    // Only a token that is *nothing but* one optional placeholder may vanish:
    // `--serial={serial}` with no serial is a broken flag, not an absent one.
    const bare = names.length === 1 && token === `{${names[0]}}`;
    const droppable = bare && specs.get(names[0] as string)?.required === false;
    matchers.push({
      droppable,
      rest: false,
      test: carriesMarker
        ? (candidate: string) => regex.test(candidate)
        : (candidate: string) => !containsLocalExecMarker(candidate) && regex.test(candidate),
    });
  }

  return { ok: true, matchers };
};

const matchSequence = (
  matchers: ReadonlyArray<TokenMatcher>,
  argv: ReadonlyArray<string>,
): boolean => {
  const go = (matcherIndex: number, argvIndex: number): boolean => {
    if (matcherIndex === matchers.length) return argvIndex === argv.length;
    const matcher = matchers[matcherIndex] as TokenMatcher;
    if (matcher.rest) return true;
    if (matcher.droppable && go(matcherIndex + 1, argvIndex)) return true;
    if (argvIndex >= argv.length) return false;
    if (!matcher.test(argv[argvIndex] as string)) return false;
    return go(matcherIndex + 1, argvIndex + 1);
  };
  return go(0, 0);
};

/**
 * Does this exact argv belong to this exact entry?
 *
 * The one check both sides run: the server before dispatching (so a bug in the
 * resolver cannot widen the whitelist) and the device before spawning, against
 * the snapshot *it* accepted rather than against anything the push carried.
 * Nothing here builds a command — it only answers yes or no about one that
 * already exists.
 */
export const matchLocalActionCommand = (
  entry: ProjectLocalAction,
  argv: ReadonlyArray<string>,
): { readonly ok: true } | { readonly ok: false; readonly reason: string } => {
  if (argv.length === 0) return { ok: false, reason: "El comando está vacío." };
  if (argv.length > LOCAL_EXEC_MAX_TOKENS) {
    return { ok: false, reason: `El comando supera los ${LOCAL_EXEC_MAX_TOKENS} argumentos.` };
  }
  for (const token of argv) {
    if (token.length === 0) return { ok: false, reason: "El comando contiene un argumento vacío." };
    if (FORBIDDEN_TOKEN_CHARS.test(token)) {
      return { ok: false, reason: "El comando contiene saltos de línea o caracteres nulos." };
    }
    if (token.length > LOCAL_EXEC_MAX_TOKEN_CHARS) {
      return {
        ok: false,
        reason: `Un argumento supera los ${LOCAL_EXEC_MAX_TOKEN_CHARS} caracteres.`,
      };
    }
  }

  const structure = validateProjectLocalAction(entry);
  if (!structure.ok) return structure;

  // A pattern entry has no file holes at all, and `**` accepts its tail without
  // testing it, so the marker guard for this kind has to be stated up front.
  if (entry.kind === "pattern" && argv.some(containsLocalExecMarker)) {
    return { ok: false, reason: "El comando no puede contener marcadores '<<taskin-…>>'." };
  }

  const built: MatcherBuild =
    entry.kind === "pattern"
      ? { ok: true, matchers: patternMatchers(entry) }
      : actionMatchers(entry);
  if (!built.ok) return built;

  return matchSequence(built.matchers, argv)
    ? { ok: true }
    : { ok: false, reason: `El comando no coincide con la entrada '${entry.id}' del catálogo.` };
};
